import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import ScissorsLight from '@/assets/scissors-light.svg';
import CopyIcon from '@/assets/copy-simple-light.svg';
import type { AnalyticsBuilderReportData } from '@/types';
import { ScrapbookContentType } from '@/types';
import { SaveToScrapbookButton } from '@/components/scrapbook';
import ProgressCircular from '@/components/general/ProgressCircular';
import classNames from 'classnames';
import { Tooltip } from '..';

type AnalyticsBuilderReportContentProps = {
  reportData: AnalyticsBuilderReportData;
  onExport: () => void;
  hideCopyToClipboardButton?: boolean;
  hideSaveToScrapbookButton?: boolean;
  hideDownloadButton?: boolean;
  className?: string;
};

const AnalyticsBuilderReportContent = ({
  reportData,
  onExport,
  hideCopyToClipboardButton,
  hideSaveToScrapbookButton,
  hideDownloadButton,
  className,
}: AnalyticsBuilderReportContentProps) => {
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [showCopied, setShowCopied] = useState(false);
  const reportContentRef = useRef<HTMLDivElement>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showActionButtons = !hideCopyToClipboardButton || !hideSaveToScrapbookButton || !hideDownloadButton;
  const isGenerating = reportData.sections.some((section) => section.status === 'loading');

  useEffect(() => {
    setExpandedSources({});
    setActiveSectionId(reportData.sections[0]?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData.id]);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    },
    [],
  );

  const scrollToSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateActiveSection = () => {
    const reportContent = reportContentRef.current;

    if (!reportContent) {
      return;
    }

    const contentTop = reportContent.getBoundingClientRect().top;
    const activationOffset = contentTop + 120;
    const activeSection =
      reportData.sections.findLast((section) => {
        const sectionElement = document.getElementById(section.id);
        return sectionElement ? sectionElement.getBoundingClientRect().top <= activationOffset : false;
      }) || reportData.sections[0];

    if (activeSection?.id && activeSection.id !== activeSectionId) {
      setActiveSectionId(activeSection.id);
    }
  };

  const getReportHtml = () => `
    <html>
      <body>
        <h1 style="font-family: Arial, Helvetica, sans-serif; font-size: 32pt; line-height: 1.2; font-weight: 700; color: #09090b; margin: 0 0 18pt;">
          ${reportData.title}
        </h1>
        ${reportData.sections
          .map(
            (section) => `
              <h2 style="font-family: Arial, Helvetica, sans-serif; font-size: 24pt; line-height: 1.25; font-weight: 700; color: #09090b; margin: 24pt 0 12pt;">
                ${section.title}
              </h2>
              ${section.html || ''}
            `,
          )
          .join('')}
      </body>
    </html>
  `;

  const getReportPlainText = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = getReportHtml();
    return tempDiv.innerText || tempDiv.textContent || '';
  };

  const handleCopyToClipboard = async () => {
    if (isGenerating) {
      return;
    }

    try {
      const formattedHtmlContent = getReportHtml();
      const formattedContent = getReportPlainText();

      if (navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([formattedHtmlContent], { type: 'text/html' }),
            'text/plain': new Blob([formattedContent], { type: 'text/plain' }),
          }),
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(formattedContent);
      }

      setShowCopied(true);

      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }

      copiedTimeoutRef.current = setTimeout(() => {
        setShowCopied(false);
      }, 2000);
    } catch {
      setShowCopied(false);
    }
  };

  const renderSectionContent = (section: AnalyticsBuilderReportData['sections'][number]) => (
    <div className="space-y-5">
      {section.html && (
        <div
          className={classNames(
            'richText space-y-5 leading-7 text-zinc-700',
            '[&_h1]:!text-5xl [&_h2]:!text-4xl [&_h3]:!text-3xl [&_h4]:!text-2xl [&_h5]:!text-xl [&_h6]:!text-lg',
            '[&_h1]:!leading-[3.5rem] [&_h2]:!leading-[3rem] [&_h3]:!leading-[2.5rem] [&_h4]:!leading-[2rem] [&_h5]:!leading-[1.5rem] [&_h6]:!leading-[1.25rem]',
            'overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_table]:text-left [&_table]:text-sm',
            '[&_th]:border [&_th]:border-zinc-200 [&_th]:bg-zinc-50 [&_th]:px-4 [&_th]:py-3 [&_th]:font-semibold [&_th]:text-zinc-900',
            '[&_td]:border [&_td]:border-zinc-200 [&_td]:px-4 [&_td]:py-3 [&_td]:align-top',
          )}
          dangerouslySetInnerHTML={{ __html: section.html }}
        />
      )}

      {section.sources && section.sources.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setExpandedSources((current) => ({ ...current, [section.id]: !current[section.id] }))}
            className="btn !text-sm !font-medium text-zinc-600 underline"
            aria-expanded={Boolean(expandedSources[section.id])}
          >
            {expandedSources[section.id] ? 'Hide sources' : `Show sources (${section.sources.length})`}
          </button>

          <AnimatePresence initial={false}>
            {expandedSources[section.id] && (
              <motion.div
                className="overflow-hidden"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
              >
                <div className="space-y-3 text-sm text-zinc-600">
                  {section.sources.map((source, index) => (
                    <div
                      key={`${source.sourceId}-${index}`}
                      className="rounded-[6px] border border-zinc-200 bg-white px-4 py-3"
                    >
                      <p className="text-md font-semibold">
                        [{index + 1}] {source.title}
                      </p>
                      {source.chunkText && <p className="mt-1 text-sm text-zinc-600">{source.chunkText}</p>}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={classNames(
        'grid h-[calc(92vh-85px)] grid-cols-1 overflow-hidden bg-zinc-50 lg:grid-cols-[1fr_260px]',
        className,
      )}
    >
      <div
        ref={reportContentRef}
        className="overflow-y-auto px-6 py-8"
        data-lenis-prevent
        onScroll={updateActiveSection}
      >
        <article className="mx-auto w-full space-y-6">
          <div className="rounded-[6px] bg-white p-8 shadow-sm ring-1 ring-zinc-100">
            <p className="mb-3 text-sm font-medium text-zinc-500">{reportData.subject}</p>
            <h1 className="text-4xl font-semibold tracking-tight text-zinc-950">{reportData.title}</h1>
          </div>

          {reportData.sections.map((section, index) => (
            <section
              key={section.id}
              id={section.id}
              className="overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-sm"
            >
              <div className="border-b border-zinc-100 bg-zinc-50 px-8 py-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-white text-xs font-semibold text-zinc-500 ring-1 ring-zinc-200">
                    {index + 1}
                  </span>
                  <h2 className="text-2xl font-semibold text-zinc-950">{section.title}</h2>
                </div>
              </div>
              <div className="p-8">
                {section.status === 'loading' ? (
                  <div className="flex items-center gap-3 text-zinc-500">
                    <ProgressCircular indeterminate size={20} width={2} colorClassName="stroke-zinc-400" />
                    <span className="text-sm font-medium">Generating…</span>
                  </div>
                ) : (
                  renderSectionContent(section)
                )}
              </div>
            </section>
          ))}
        </article>
      </div>

      <aside className="hidden h-full overflow-hidden border-l border-zinc-100 bg-white p-6 lg:flex lg:flex-col">
        <div>
          <p className="mb-4 text-sm font-semibold text-zinc-950">On this report</p>
          <nav className="space-y-2 text-sm text-zinc-600">
            {reportData.sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollToSection(section.id)}
                className={classNames(
                  'flex w-full cursor-pointer items-center justify-between gap-2 rounded-[6px] px-3 py-2 text-left hover:bg-zinc-50',
                  activeSectionId === section.id && 'bg-zinc-100 font-medium text-zinc-950',
                )}
              >
                <span className="truncate">{section.title}</span>
                {section.status === 'loading' && (
                  <ProgressCircular
                    indeterminate
                    size={14}
                    width={2}
                    colorClassName="stroke-zinc-400"
                    className="shrink-0"
                  />
                )}
              </button>
            ))}
          </nav>
        </div>

        {showActionButtons && (
          <div className="mt-auto flex flex-col gap-2 border-t border-zinc-100 pt-5">
            {!hideCopyToClipboardButton && (
              <div className="relative">
                {showCopied && (
                  <div className="absolute -top-10 left-0 flex w-full justify-center text-center text-sm font-medium text-black">
                    <div className="w-auto rounded-md bg-white p-2">Copied...</div>
                  </div>
                )}
                <button
                  type="button"
                  className="btn secondary w-full !py-2.5"
                  onClick={handleCopyToClipboard}
                  disabled={isGenerating}
                >
                  <CopyIcon className="mt-0.5 h-5 w-5 !fill-black" />
                  <span className="hidden text-sm md:block">{'Copy to Clipboard'}</span>
                </button>
              </div>
            )}

            {!hideSaveToScrapbookButton && (
              <SaveToScrapbookButton
                scrapbookContent={{
                  ...reportData,
                }}
                scrapbookContentType={ScrapbookContentType.AnalyticsBuilder}
                scrapbookPath={`analyticsBuilder.${reportData.id}.json`}
                successMessage="Analytics Builder content was added to scrapbook successfully"
                tooltipProps={{ inactive: true, content: ' ' }}
                className="btn secondary"
                disabled={isGenerating}
              >
                <ScissorsLight className="mt-0.5 h-5 w-5 !fill-black" />
                <span className="mt-px hidden text-sm md:block">{'Save to Scrapbook'}</span>
              </SaveToScrapbookButton>
            )}

            {!hideDownloadButton && (
              <Tooltip placement="top" content="Coming soon">
                <button
                  disabled
                  className="btn primary w-full !cursor-not-allowed !border-gray-300 !bg-gray-300 !py-2.5 hover:!text-white"
                  // className="btn primary w-full !py-2.5 hover:!bg-black/80 hover:!text-white"
                  onClick={onExport}
                >
                  <DownloadSimpleLight className="h-5 w-5 !fill-white" />
                  <span className="hidden text-sm md:block">{'Download'}</span>
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default AnalyticsBuilderReportContent;
